<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { ForecastVariable } from '../internal/types.ts'
import { formatDate } from '@taler/domain'
import { ActionButton } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  variables: readonly ForecastVariable[]
  deletingId: string | undefined
}>()
const emit = defineEmits<{ delete: [variableId: string] }>()

const { locale, t } = useI18n()

const rows = computed(() =>
  props.variables.map((variable) => {
    const loc = locale.value as 'en' | 'hu'
    return {
      variable,
      amount: variable.amount,
      period:
        variable.endDate === undefined
          ? t('forecast.variablePeriodOpen', { start: formatDate(variable.startDate, loc) })
          : t('forecast.variablePeriodClosed', {
              start: formatDate(variable.startDate, loc),
              end: formatDate(variable.endDate, loc),
            }),
    }
  }),
)
</script>

<template>
  <div>
    <p v-if="variables.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('forecast.noVariables') }}
    </p>
    <ul v-else class="flex flex-col divide-y divide-[color:var(--color-border)]">
      <li
        v-for="row in rows"
        :key="row.variable.id"
        class="flex items-center justify-between gap-[var(--space-cell)] py-[var(--space-inline)]"
      >
        <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
          <span class="text-[color:var(--color-text)]">{{ row.variable.name }}</span>
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            row.period
          }}</span>
        </div>
        <div class="flex shrink-0 items-center gap-[var(--space-group)]">
          <MoneyAmount :value="row.amount" sign />
          <ActionButton
            type="button"
            variant="quiet"
            :disabled="deletingId === row.variable.id"
            :aria-label="`${t('forecast.deleteAction')} ${row.variable.name}`"
            @click="emit('delete', row.variable.id)"
          >
            {{ deletingId === row.variable.id ? t('forecast.deleting') : t('forecast.deleteAction') }}
          </ActionButton>
        </div>
      </li>
    </ul>
  </div>
</template>
